import { useEffect, useRef, useState } from "react";
import { selectBooking, selectBookingType, useClientPortalSelector } from "../../../../redux";
import { BookingStatus } from "../../bookingSlice.types";
import { useValidateBooking } from "../../hooks/useValidateBooking";
import { BookingType } from "types/appointmentBooking";

export interface IUsePaymentAlreadyReceivedHook {
    paymentAlreadyReceived: boolean;
    isChecking: boolean;
}

interface ICheckResult {
    key: string;
    paymentAlreadyReceived: boolean;
}

// A client who cancelled a paid class seat in the portal still owns that payment: the server
// restores the seat together with its paid invoice, so the portal must not collect payment
// again. Only class bookings can restore a seat, so the check runs for them alone.
export function usePaymentAlreadyReceived(): IUsePaymentAlreadyReceivedHook {
    const bookingType = useClientPortalSelector(selectBookingType);
    const { availability, confirmation, session } = useClientPortalSelector(selectBooking);
    const { validateBooking } = useValidateBooking();

    const validateBookingRef = useRef(validateBooking);
    useEffect(() => {
        validateBookingRef.current = validateBooking;
    });

    const appointmentId = availability.data?.timeEntry?.appointmentId;
    const clientId = confirmation.data?.client?.id;
    const shouldCheck =
        bookingType === BookingType.CLASS && !!appointmentId && session.bookingStatus !== BookingStatus.BOOKED;
    const key = `${appointmentId ?? ""}:${clientId ?? ""}`;

    const [result, setResult] = useState<ICheckResult | null>(null);

    useEffect(() => {
        if (!shouldCheck) {
            return;
        }

        let cancelled = false;
        validateBookingRef
            .current({ silent: true })
            .then((response) => {
                if (!cancelled) {
                    setResult({ key, paymentAlreadyReceived: !!response?.paymentAlreadyReceived });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setResult({ key, paymentAlreadyReceived: false });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [shouldCheck, key]);

    const hasCurrentResult = result?.key === key;

    return {
        paymentAlreadyReceived: shouldCheck && hasCurrentResult && result.paymentAlreadyReceived,
        isChecking: shouldCheck && !hasCurrentResult,
    };
}
